/**
 * Firestore sync for Payment Receipts.
 * Uses collections/receipts. Real-time listener keeps local state in sync.
 */
import {
  collection,
  doc,
  getDocs,
  setDoc,
  deleteDoc,
  onSnapshot,
  serverTimestamp,
  query,
  orderBy,
} from 'firebase/firestore';
import { db } from '../firebase_analytics';

const COLLECTION = 'receipts';
const MIGRATION_KEY = 'receipts_migrated_to_firestore';

function receiptsRef() {
  return collection(db, COLLECTION);
}

function receiptDoc(id) {
  return doc(db, COLLECTION, id);
}

function toFirestore(receipt) {
  const { id, ...rest } = receipt;
  return {
    ...rest,
    updatedAt: serverTimestamp(),
  };
}

function fromFirestore(id, data) {
  if (!data) return null;
  return {
    id,
    ...data,
    createdAt: data.createdAt?.toDate?.()?.toISOString?.() ?? data.createdAt,
    updatedAt: data.updatedAt?.toDate?.()?.toISOString?.() ?? data.updatedAt,
  };
}

/**
 * One-time migration: push existing localStorage receipts to Firestore.
 */
export async function migrateReceiptsFromLocalStorage() {
  if (typeof window === 'undefined') return;
  if (localStorage.getItem(MIGRATION_KEY)) return;

  const raw = localStorage.getItem('paymentReceipts');
  if (!raw) {
    localStorage.setItem(MIGRATION_KEY, 'true');
    return;
  }

  let list;
  try {
    list = JSON.parse(raw);
  } catch {
    localStorage.setItem(MIGRATION_KEY, 'true');
    return;
  }

  if (!Array.isArray(list) || list.length === 0) {
    localStorage.setItem(MIGRATION_KEY, 'true');
    return;
  }

  for (const rec of list) {
    const id = rec.id || `rcp_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    const payload = toFirestore({ ...rec, id });
    if (!payload.createdAt) payload.createdAt = serverTimestamp();
    await setDoc(receiptDoc(id), payload, { merge: true });
  }

  localStorage.setItem(MIGRATION_KEY, 'true');
}

export async function getReceipts() {
  const q = query(receiptsRef(), orderBy('updatedAt', 'desc'));
  const snap = await getDocs(q);
  return snap.docs.map((d) => fromFirestore(d.id, d.data()));
}

export function subscribeReceipts(callback) {
  const q = query(receiptsRef(), orderBy('updatedAt', 'desc'));
  return onSnapshot(q, (snap) => {
    const list = snap.docs.map((d) => fromFirestore(d.id, d.data()));
    callback(list);
  });
}

export async function saveReceipt(receipt) {
  const id = receipt.id || `rcp_${Date.now()}_${Math.random().toString(36).slice(2)}`;
  const payload = toFirestore({ ...receipt, id });
  if (!payload.createdAt) payload.createdAt = serverTimestamp();
  await setDoc(receiptDoc(id), payload, { merge: true });
  return id;
}

export async function deleteReceipt(id) {
  await deleteDoc(receiptDoc(id));
}
